import { useEffect } from 'react';
import SEOHead from '../../components/SEOHead';

export default function SeasonEnded() {
  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <SEOHead
        title="Temporada Finalizada - Starlight Christmas"
        description="La temporada navideña ha terminado. Gracias por ser parte de esta experiencia mágica. Nos vemos el próximo año."
        keywords="fotografía navideña, sesiones familiares, próxima temporada"
        url={`${import.meta.env.VITE_SITE_URL}/season-ended`}
      />
      
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Fondo estrellado animado */}
        <div className="absolute inset-0 bg-black">
          <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900/50 to-black"></div>
          
          {/* Estrellas doradas */}
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 2}s`
              }}
            >
              <div className="w-0.5 h-0.5 sm:w-1 sm:h-1 rounded-full bg-gold-500 shadow-lg" style={{ boxShadow: '0 0 10px #d5ab4c80' }}></div>
            </div>
          ))}

          {/* Destellos mágicos */}
          {[...Array(15)].map((_, i) => (
            <div
              key={`sparkle-${i}`}
              className="absolute animate-ping"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 4}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            >
              <div className="w-1 h-1 sm:w-2 sm:h-2 rounded-full bg-gold-500 shadow-xl" style={{ boxShadow: '0 0 20px #d5ab4c70' }}></div>
            </div>
          ))}
        </div>

        {/* Contenido principal */}
        <div className="relative z-10 text-center px-4 sm:px-6 md:px-8 lg:px-12 max-w-5xl mx-auto w-full">
          {/* Logo */}
          <div className="mb-8 sm:mb-12 md:mb-16">
            <img 
              src="https://static.readdy.ai/image/d7e77f925f0119a01fe5d8699c3ff655/df42e42088fea8669f9dab7c63fea457.png" 
              alt="Starlight Christmas Photo Session" 
              className="w-64 sm:w-80 md:w-96 lg:w-[450px] h-auto mx-auto drop-shadow-2xl max-w-full"
            />
          </div>

          {/* Mensaje principal */}
          <div className="mb-8 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif text-gold-500 mb-6 sm:mb-8 leading-tight">
              Esta Temporada Ha Terminado
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-200 leading-relaxed font-sans font-light mb-6 sm:mb-8">
              Gracias por ser parte de esta experiencia mágica.
            </p>
            <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-gold-400 font-serif font-semibold">
              Nos vemos el próximo año ✨
            </p>
          </div>

          {/* Elementos decorativos */}
          <div className="flex justify-center items-center space-x-4 sm:space-x-6 md:space-x-8 mt-12 sm:mt-16">
            <div className="w-12 sm:w-16 md:w-20 lg:w-24 h-px bg-gradient-to-r from-transparent via-gold-500 to-transparent"></div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 flex items-center justify-center">
              <i className="ri-star-fill text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gold-500 animate-pulse"></i>
            </div>
            <div className="w-12 sm:w-16 md:w-20 lg:w-24 h-px bg-gradient-to-r from-transparent via-gold-500 to-transparent"></div>
          </div>

          {/* Información de contacto */}
          <div className="mt-16 sm:mt-20 md:mt-24">
            <p className="text-base sm:text-lg md:text-xl text-gray-300 font-sans mb-4">
              Para consultas o información sobre la próxima temporada:
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-8">
              <a 
                href="tel:787-459-9755" 
                className="flex items-center text-gold-400 hover:text-gold-300 transition-colors duration-300 text-base sm:text-lg cursor-pointer"
              >
                <i className="ri-phone-line text-xl sm:text-2xl mr-2"></i>
                787-459-9755
              </a>
              <a 
                href="mailto:info@idoelphotography.com" 
                className="flex items-center text-gold-400 hover:text-gold-300 transition-colors duration-300 text-base sm:text-lg cursor-pointer"
              >
                <i className="ri-mail-line text-xl sm:text-2xl mr-2"></i>
                info@idoelphotography.com
              </a>
            </div>
          </div>

          {/* Redes sociales */}
          <div className="mt-12 sm:mt-16">
            <p className="text-base sm:text-lg text-gray-300 font-sans mb-6">
              Síguenos en redes sociales:
            </p>
            <div className="flex justify-center items-center gap-6 sm:gap-8">
              <a 
                href="https://www.instagram.com/idoelphotography" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-full border-2 border-gold-500 text-gold-500 hover:bg-gold-500 hover:text-black transition-all duration-300 cursor-pointer"
              >
                <i className="ri-instagram-line text-2xl sm:text-3xl"></i>
              </a>
              <a 
                href="http://facebook.com/idoelphotography" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-full border-2 border-gold-500 text-gold-500 hover:bg-gold-500 hover:text-black transition-all duration-300 cursor-pointer"
              >
                <i className="ri-facebook-fill text-2xl sm:text-3xl"></i>
              </a>
              <a 
                href="https://www.threads.com/@idoelphotography" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-full border-2 border-gold-500 text-gold-500 hover:bg-gold-500 hover:text-black transition-all duration-300 cursor-pointer"
              >
                <i className="ri-at-line text-2xl sm:text-3xl"></i>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
